import { motion } from "motion/react";
import { SEO } from "../components/seo";
import { Mail, MessageSquare, Calendar, Github, Linkedin, Twitter, MapPin, Clock, Globe } from "lucide-react";
import { useState } from "react";
import { projectId, publicAnonKey } from "/utils/supabase/info";
import { toast } from "sonner";

export function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sending");

    try {
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-5f13ee94/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${publicAnonKey}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      setStatus("success");
      toast.success("Message sent successfully!", {
        description: "Thanks for reaching out, I'll get back to you soon.",
      });
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      console.error("Error sending message:", error);
      setStatus("error");
      toast.error("Error sending message", {
        description: "Please try again later or email me directly.",
      });
    } finally {
      setTimeout(() => setStatus("idle"), 3000);
    }
  };

  const contactMethods = [
    {
      icon: Mail,
      title: "Email",
      description: "Send me an email anytime",
      value: "ergysonuzi12@gmail.com",
      link: "mailto:ergysonuzi12@gmail.com",
      color: "from-violet-500 to-fuchsia-500",
    },
    {
      icon: MessageSquare,
      title: "Quick Chat",
      description: "Let's have a conversation",
      value: "Schedule a call",
      link: "#contact-form",
      color: "from-fuchsia-500 to-cyan-500",
    },
    {
      icon: Calendar,
      title: "Consultation",
      description: "Book a free consultation",
      value: "30-min session",
      link: "#contact-form",
      color: "from-cyan-500 to-violet-500",
    },
  ];

  const socialLinks = [
    { icon: Github, name: "GitHub", url: "https://github.com/ergysonuzi", color: "hover:text-violet-400" },
    { icon: Linkedin, name: "LinkedIn", url: "https://linkedin.com/in/ergysonuzi", color: "hover:text-fuchsia-400" },
    { icon: Twitter, name: "Twitter", url: "https://twitter.com/ergysonuzi", color: "hover:text-cyan-400" },
  ];

  const info = [
    { icon: MapPin, label: "Location", value: "Available Remote & On-site" },
    { icon: Clock, label: "Timezone", value: "UTC+1 (CET)" },
    { icon: Mail, label: "Response Time", value: "Within 24 hours" },
  ];

  const serviceAreas = [
    { region: "Prizren, Kosovo", focus: "Local Business Solutions & On-site Support" },
    { region: "Prishtina, Kosovo", focus: "Enterprise Systems & Technical Consulting" },
    { region: "European Union", focus: "Remote Development & Digital Transformation" },
    { region: "Worldwide", focus: "Custom Web Applications & API Integrations" },
  ];

  return (
    <>
      <SEO 
        title="Contact - Let's Work Together" 
        description="Get in touch with Ergys Onuzi for web development, design, and business solutions. Available for freelance projects and consultations."
        canonical="/contact"
      />

      <div className="min-h-screen bg-black text-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-32 pb-20 px-6">
          {/* Animated Background */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 right-1/4 w-96 h-96 bg-violet-500 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-cyan-500 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>

          <div className="max-w-4xl mx-auto relative text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
                Let's Connect
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
                Have a project in mind? I'd love to hear about it.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="py-12 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {contactMethods.map((method, index) => (
                <motion.a
                  key={method.title}
                  href={method.link}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group relative overflow-hidden bg-gradient-to-br from-violet-950/40 via-fuchsia-950/40 to-cyan-950/40 rounded-2xl p-8 border border-violet-500/20 hover:border-violet-400/50 transition-all duration-300 hover:scale-105"
                >
                  <div className="relative">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${method.color} flex items-center justify-center mb-4`}>
                      <method.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-white">{method.title}</h3>
                    <p className="text-gray-400 text-sm mb-3">{method.description}</p>
                    <p className="text-violet-400 font-semibold">{method.value}</p>
                  </div>
                  <div className={`absolute inset-0 bg-gradient-to-br ${method.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* Main Contact Form & Info */}
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-2"
                id="contact-form"
              >
                <div className="bg-gradient-to-br from-violet-950/20 via-fuchsia-950/20 to-cyan-950/20 rounded-3xl p-8 md:p-12 border border-violet-500/20 backdrop-blur-sm">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                    Send a Message
                  </h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-gray-300 mb-2">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 bg-black/50 border border-violet-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-violet-400 transition-colors"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 bg-black/50 border border-violet-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-violet-400 transition-colors"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-semibold text-gray-300 mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        id="subject"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-4 py-3 bg-black/50 border border-violet-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-violet-400 transition-colors"
                        placeholder="Project Inquiry"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-gray-300 mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={6}
                        className="w-full px-4 py-3 bg-black/50 border border-violet-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-violet-400 transition-colors resize-none"
                        placeholder="Tell me about your project..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={status === "sending"}
                      className="w-full px-8 py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl font-semibold hover:from-violet-500 hover:to-fuchsia-500 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-violet-500/50"
                    >
                      {status === "sending" ? "Sending..." : status === "success" ? "Message Sent! ✓" : "Send Message"}
                    </button>
                  </form>
                </div>
              </motion.div>

              {/* Contact Info Sidebar */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                {/* Info Cards */}
                <div className="bg-gradient-to-br from-violet-950/20 via-fuchsia-950/20 to-cyan-950/20 rounded-2xl p-6 border border-violet-500/20">
                  <h3 className="text-xl font-bold mb-6 text-white">Quick Info</h3>
                  <div className="space-y-4">
                    {info.map((item) => (
                      <div key={item.label} className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 flex items-center justify-center flex-shrink-0">
                          <item.icon className="w-5 h-5 text-violet-400" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">{item.label}</p>
                          <p className="text-white font-semibold">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Service Area */}
                <div className="bg-gradient-to-br from-violet-950/20 via-fuchsia-950/20 to-cyan-950/20 rounded-2xl p-6 border border-violet-500/20">
                  <h3 className="text-xl font-bold mb-6 text-white flex items-center gap-2">
                    <Globe size={20} className="text-cyan-400" /> Service Area
                  </h3>
                  <div className="space-y-4">
                    {serviceAreas.map((area) => (
                      <div key={area.region} className="border-l-2 border-violet-500/30 pl-4">
                        <p className="text-white font-bold text-sm uppercase tracking-tight">{area.region}</p>
                        <p className="text-xs text-neutral-500">{area.focus}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Social Links */}
                <div className="bg-gradient-to-br from-violet-950/20 via-fuchsia-950/20 to-cyan-950/20 rounded-2xl p-6 border border-violet-500/20">
                  <h3 className="text-xl font-bold mb-6 text-white">Connect</h3>
                  <div className="space-y-3">
                    {socialLinks.map((social) => (
                      <a
                        key={social.name}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-3 p-3 rounded-lg bg-black/30 hover:bg-black/50 transition-all duration-300 group ${social.color}`}
                      >
                        <social.icon className="w-5 h-5 text-gray-400 group-hover:scale-110 transition-transform" />
                        <span className="text-gray-300 group-hover:text-white transition-colors">{social.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}